import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { Box } from '@mui/material';
import { Sidebar } from './Sidebar';
import { ErrorBoundary } from '../common/ErrorBoundary';
import { CommandPalette } from '../common/CommandPalette';
import { ShortcutsHelpModal } from '../common/ShortcutsHelpModal';
import { OnboardingTour } from '../common/OnboardingTour';

function isOnboardingCompleted(): boolean {
  try {
    return localStorage.getItem('onboarding_completed') === 'true';
  } catch {
    return false;
  }
}

export function AppShell({ children }: { children: ReactNode }) {
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  const [showTour, setShowTour] = useState(() => !isOnboardingCompleted());

  const handleShortcutsClose = useCallback(() => setShortcutsOpen(false), []);

  // Open shortcuts help with "?" when no input is focused
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key !== '?') return;
      const tag = (e.target as HTMLElement)?.tagName;
      const isEditable =
        tag === 'INPUT' ||
        tag === 'TEXTAREA' ||
        tag === 'SELECT' ||
        (e.target as HTMLElement)?.isContentEditable;
      if (isEditable) return;
      e.preventDefault();
      setShortcutsOpen((prev) => !prev);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <Sidebar />
      <Box
        component="main"
        sx={{ flex: 1, p: 3, overflow: 'auto', minWidth: 0 }}
      >
        <ErrorBoundary>{children}</ErrorBoundary>
      </Box>
      <CommandPalette />
      <ShortcutsHelpModal open={shortcutsOpen} onClose={handleShortcutsClose} />
      <OnboardingTour open={showTour} onComplete={() => setShowTour(false)} />
    </Box>
  );
}
